import React from "react";

import { useTranslation } from "react-i18next";

import LinkSidebarTransition from "@Shared/Components/LinkSidebar";
import IconsSidebar from "@Shared/Components/IconsSidebar";

import TextArea from "../../../../../../components/ui/input/text-area/TextArea";
import ColorPicker from "../../../../../../components/ui/color-picker/ColorPicker";
import ButtonGroup from "../../../../../../components/ui/button-group/ButtonGroup";
import LinkPicker from "../../../../../../components/ui/link-picker/LinkPicker";
import Text from "../../../../../../components/ui/input/text/Input";
import IconPicker from "../../../../../../components/ui/icon-picker/IconPicker";
import FormatPicker from "../../../../../../components/ui/input/time/FormatPicker";
import { DateTime } from "../../../../../../components/ui/input/datetime/DateTime";
import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
} from "../../styles";
import { BOOLEAN_OPTIONS, CORNERS_OPTIONS } from "./options";
import { COUNTDOWN_PROPS, DESIGN_OPTIONS, DESIGN_STYLES } from "./defaults";
import { useCountdownStore } from "../../../../../../stores/use-countdown-store";
import ImagePicker from "../../../../../../components/ui/image-picker/ImagePicker";

const CountdownSettings = () => {
  const { t } = useTranslation();
  const { data, setData, action, setAction } = useCountdownStore((state) => ({
    data: state.data,
    setData: state.setData,
    action: state.action,
    setAction: state.setAction,
  }));

  const handleTimerLayoutChange = (isOn, prop) => {
    let timerLayout = [];
    if (isOn) {
      timerLayout = [...data[COUNTDOWN_PROPS.TIMER_LAYOUT]];
      timerLayout.push({ value: prop });
    } else {
      timerLayout = data[COUNTDOWN_PROPS.TIMER_LAYOUT].filter(
        (layout) => layout.value !== prop
      );
    }
    setData({
      [COUNTDOWN_PROPS.TIMER_LAYOUT]: timerLayout,
    });
  };

  return (
    <>
      {action && action.drawer === "link" && (
        <LinkSidebarTransition
          isOpen={action.drawer === "link"}
          setIsOpen={() => setAction(undefined)}
          value={data[COUNTDOWN_PROPS.URL]}
          onChange={(value) => {
            setData({ [COUNTDOWN_PROPS.URL]: value });
            setAction(undefined);
          }}
        />
      )}

      {action && action.drawer === "icon" && (
        <IconsSidebar
          isOpen={action.drawer === "icon"}
          setIsOpen={() => setAction(undefined)}
          containerStyles={{ top: "75px" }}
          onEmojiChange={(value) => setData({ [COUNTDOWN_PROPS.ICON]: value })}
          onIconChange={(value) => setData({ [COUNTDOWN_PROPS.ICON]: value })}
          onAppChange={(value) => setData({ [COUNTDOWN_PROPS.ICON]: value })}
          selectedIcon={data[COUNTDOWN_PROPS.ICON]}
          tabs={["Icons", "Emojis", "Logos"]}
          activeTab={data[COUNTDOWN_PROPS.ICON]?.native ? "Emojis" : "Icons"}
        />
      )}

      {DESIGN_OPTIONS.ARROW.includes(data[COUNTDOWN_PROPS.TIMER_TYPE]) && (
        <SettingsOptionContainer>
          <SettingsOptionHeading>{t("image")}</SettingsOptionHeading>
          <ImagePicker
            hasImageReposition
            image={data[COUNTDOWN_PROPS.IMAGE]}
            onChange={(value) => {
              console.log("value", value);
              setData({ [COUNTDOWN_PROPS.IMAGE]: value });
            }}
          />
        </SettingsOptionContainer>
      )}

      <SettingsOptionContainer>
        <SettingsOptionHeading>
          {t("flexible-display-items")}
        </SettingsOptionHeading>
        {DESIGN_OPTIONS.ICON.includes(data[COUNTDOWN_PROPS.TIMER_TYPE]) && (
          <OptionContainer>
            <OptionLabel>{t("icon")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={
                  !!data[COUNTDOWN_PROPS.TIMER_LAYOUT].find(
                    (layout) => layout.value === "icon"
                  )
                }
                onClick={(isOn) => handleTimerLayoutChange(isOn, "icon")}
              />
            </OptionContent>
          </OptionContainer>
        )}

        <OptionContainer>
          <OptionLabel>{t("subtext")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={
                !!data[COUNTDOWN_PROPS.TIMER_LAYOUT].find(
                  (layout) => layout.value === "text"
                )
              }
              onClick={(isOn) => handleTimerLayoutChange(isOn, "text")}
            />
          </OptionContent>
        </OptionContainer>

        <OptionContainer>
          <OptionLabel>
            {data[COUNTDOWN_PROPS.TIMER_TYPE] !== DESIGN_STYLES.SMALL_COUNTDOWN
              ? t("label")
              : t("link-text")}
          </OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={
                !!data[COUNTDOWN_PROPS.TIMER_LAYOUT].find(
                  (layout) => layout.value === "label"
                )
              }
              onClick={(isOn) => handleTimerLayoutChange(isOn, "label")}
            />
          </OptionContent>
        </OptionContainer>

        {DESIGN_OPTIONS.ARROW.includes(data[COUNTDOWN_PROPS.TIMER_TYPE]) && (
          <OptionContainer>
            <OptionLabel>{t("arrow")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={
                  !!data[COUNTDOWN_PROPS.TIMER_LAYOUT].find(
                    (layout) => layout.value === "arrow"
                  )
                }
                onClick={(isOn) => handleTimerLayoutChange(isOn, "arrow")}
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("link")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel alignToTop>{t("link-to")}</OptionLabel>
          <OptionContent>
            <LinkPicker
              value={data[COUNTDOWN_PROPS.URL]}
              onChange={(value) => setData({ [COUNTDOWN_PROPS.URL]: value })}
            />
          </OptionContent>
        </OptionContainer>
        <OptionContainer>
          <OptionLabel>{t("new-tab")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={data[COUNTDOWN_PROPS.NEW_TAB]}
              onClick={(value) => setData({ [COUNTDOWN_PROPS.NEW_TAB]: value })}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("content")}</SettingsOptionHeading>
        {data[COUNTDOWN_PROPS.TIMER_TYPE] !== DESIGN_STYLES.SMALL_COUNTDOWN &&
          data[COUNTDOWN_PROPS.TIMER_LAYOUT].find(
            (layout) => layout.value === "icon"
          ) && (
            <OptionContainer>
              <OptionLabel>{t("icon")}</OptionLabel>
              <OptionContent>
                <IconPicker
                  defaultTab={
                    data[COUNTDOWN_PROPS.ICON]?.native ? "Emojis" : "Icons"
                  }
                  tabs={["Icons", "Emojis", "Logos"]}
                  icon={data[COUNTDOWN_PROPS.ICON]}
                  onChange={({ value }) =>
                    setData({ [COUNTDOWN_PROPS.ICON]: value })
                  }
                />
              </OptionContent>
            </OptionContainer>
          )}
        <OptionContainer>
          <OptionLabel>{t("title")}</OptionLabel>
          <OptionContent>
            <Text
              placeholder="Add..."
              value={data[COUNTDOWN_PROPS.TITLE]}
              onChange={(value) => setData({ [COUNTDOWN_PROPS.TITLE]: value })}
            />
          </OptionContent>
        </OptionContainer>
        {!!data[COUNTDOWN_PROPS.TIMER_LAYOUT].find(
          (layout) => layout.value === "text"
        ) && (
          <OptionContainer>
            <OptionLabel>{t("subtext")}</OptionLabel>
            <OptionContent>
              <TextArea
                placeholder="Add..."
                value={data[COUNTDOWN_PROPS.TEXT]}
                onChange={({ target: { value } }) =>
                  setData({ [COUNTDOWN_PROPS.TEXT]: value })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}
        {!!data[COUNTDOWN_PROPS.TIMER_LAYOUT].find(
          (layout) => layout.value === "label"
        ) && (
          <OptionContainer>
            <OptionLabel>
              {data[COUNTDOWN_PROPS.TIMER_TYPE] !==
              DESIGN_STYLES.SMALL_COUNTDOWN
                ? t("label")
                : t("link-text")}
            </OptionLabel>
            <OptionContent>
              <Text
                placeholder="Add..."
                value={data[COUNTDOWN_PROPS.LINK_TEXT]}
                onChange={(value) =>
                  setData({ [COUNTDOWN_PROPS.LINK_TEXT]: value })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}
        <OptionContainer
          style={{ flexDirection: "column", alignItems: "flex-start" }}
        >
          <OptionContainer style={{ width: "100%" }}>
            <OptionLabel>{t("date-time")}</OptionLabel>
            <FormatPicker
              value={
                data[COUNTDOWN_PROPS.FORMAT] === "24" ? "HH:mm" : "hh:mm A"
              }
              setValue={(value) =>
                setData({
                  [COUNTDOWN_PROPS.FORMAT]: value === "HH:mm" ? "24" : "12",
                })
              }
            />
          </OptionContainer>
          <OptionContent style={{ width: "100%" }}>
            <DateTime
              value={data[COUNTDOWN_PROPS.TIME]}
              timeFormat={
                data[COUNTDOWN_PROPS.FORMAT] === "24" ? "HH:mm" : "hh:mm A"
              }
              onChange={(time) => setData({ [COUNTDOWN_PROPS.TIME]: time })}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("styling-options")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("corners")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={CORNERS_OPTIONS}
              value={data[COUNTDOWN_PROPS.BORDER_RADIUS]}
              options={{ input: true }}
              onClick={(value) =>
                setData({ [COUNTDOWN_PROPS.BORDER_RADIUS]: value })
              }
            />
          </OptionContent>
        </OptionContainer>
        {data[COUNTDOWN_PROPS.TIMER_TYPE] === DESIGN_STYLES.CUSTOM_COLOR && (
          <OptionContainer>
            <OptionLabel>{t("color-mute")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={data[COUNTDOWN_PROPS.COLOR_MUTE]}
                onClick={(value) =>
                  setData({ [COUNTDOWN_PROPS.COLOR_MUTE]: value })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}
        <OptionContainer>
          <OptionLabel alignToTop>{t("colors")}</OptionLabel>
          <OptionContent>
            <ColorPicker
              hideColorTiles
              value={{
                accent: data?.colorFields?.Timer,
                text: data?.colorFields?.Timer,
              }}
              onChange={({ accent }) =>
                setData({ colorFields: { Timer: accent } })
              }
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>
    </>
  );
};

export default CountdownSettings;
